import { useMfrrConfig } from 'api/mfrr/mfrr.api'
import type { MfrrMarket } from 'api/mfrr/mfrr.types'
import {
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useState,
  useSyncExternalStore,
} from 'react'
import { useTranslation } from 'react-i18next'
import { MfrrDateRangeOption } from 'store/mfrr/mfrr'
import { Switch } from 'ui/atoms'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import InfoBox from 'ui/components/ModuleHeader/InfoBox'
import { DatePicker } from 'ui/molecules/pickers/DatePicker/DatePicker'
import Datetime from 'utils/datetime/datetime'
import type { MfrrDateRangeSwitchItem } from '../../MFRRTradingView.types'
import Deadline from '../MFRRTradingDeadlineCounter/MFRRTradingDeadlineCounter'
import {
  getCurrentMinuteMarker,
  hasCMMarketClosedForDeliveryDate,
  hasEAMMarketClosedForDeliveryDate,
  isMfrrDateTooFarInFuture,
  parseTimeString,
  subscribeToMinuteBoundary,
} from '../MFRRTradingDeadlineCounter/MFRRTradingDeadlineCounter.helper'
import styles from './MFRRTradingViewHeader.module.less'

type MFRRTradingViewHeaderProps = {
  infoHeader: string | React.JSX.Element
  onInfoButtonClick?: () => void
  beta?: boolean
  secondaryChildren?: ReactNode
  switchItems: MfrrDateRangeSwitchItem[]
  switchValue: MfrrDateRangeOption
  onSwitchValueChange: (value: MfrrDateRangeOption) => void
  date: { startTime: ISODateTime; endTime: ISODateTime }
  setDate: (startTime: ISODateTime, endTime: ISODateTime) => void
  activeMarket: MfrrMarket | null
}

export default function MFRRTradingViewHeader({
  infoHeader,
  onInfoButtonClick,
  children,
  beta,
  secondaryChildren,
  switchItems,
  switchValue,
  onSwitchValueChange,
  date,
  setDate,
  activeMarket,
}: PropsWithChildren<MFRRTradingViewHeaderProps>): ReactElement {
  const { t } = useTranslation()
  const { activeSystem } = useAuth()
  const [hasDeadlineWarning, setHasDeadlineWarning] = useState(false)
  const handleDeadlineWarningChange = useCallback((hasWarning: boolean) => {
    setHasDeadlineWarning(hasWarning)
  }, [])

  const { data: mfrrConfig } = useMfrrConfig(activeSystem?.id)
  const cmGateClosureTimeComponents = mfrrConfig?.cm_gate_closure_time
    ? parseTimeString(mfrrConfig.cm_gate_closure_time)
    : undefined
  const minuteMarker = useSyncExternalStore(subscribeToMinuteBoundary, getCurrentMinuteMarker, getCurrentMinuteMarker)
  const mfrrTradingDeadlineCounter = minuteMarker * 60_000

  useEffect(() => {
    const hourOffsetByValue: Partial<Record<MfrrDateRangeOption, number>> = {
      today: 0,
      tomorrow: 24,
      dayAfterTomorrow: 48,
    }
    const hourOffset = hourOffsetByValue[switchValue]
    if (hourOffset === undefined) {
      return
    }
    const isoNow = Datetime.getISONow(hourOffset)
    setDate(Datetime.getStartOfDay(isoNow), Datetime.getEndOfDay(isoNow))
  }, [setDate, switchValue])

  const hasCMMarketClosedForSelectedDate = hasCMMarketClosedForDeliveryDate(
    date.startTime,
    mfrrTradingDeadlineCounter,
    cmGateClosureTimeComponents,
    mfrrConfig?.cm_input_margin_closure_time,
    mfrrConfig?.cm_lead_days
  )

  const hasEAMMarketClosedForSelectedDate = hasEAMMarketClosedForDeliveryDate(
    date.startTime,
    mfrrTradingDeadlineCounter,
    mfrrConfig?.eam_gate_closure_minutes_before,
    mfrrConfig?.eam_input_margin_closure_time
  )

  const maxBidDaysAheadForActiveMarket =
    activeMarket === 'cm'
      ? mfrrConfig?.cm_max_bid_days_ahead
      : activeMarket === 'eam'
        ? mfrrConfig?.eam_max_bid_days_ahead
        : undefined

  const isDateTooFarInFutureForActiveMarket =
    activeMarket === null
      ? false
      : isMfrrDateTooFarInFuture(date.startTime, mfrrTradingDeadlineCounter, maxBidDaysAheadForActiveMarket)

  const hasMarketClosedForSelectedDate =
    activeMarket === 'cm'
      ? hasCMMarketClosedForSelectedDate
      : activeMarket === 'eam'
        ? hasEAMMarketClosedForSelectedDate
        : false

  const shouldShowWarning =
    activeMarket !== null && (isDateTooFarInFutureForActiveMarket || hasMarketClosedForSelectedDate)

  const warningText = isDateTooFarInFutureForActiveMarket
    ? activeMarket === 'cm'
      ? t('The selected date is too far into the future for mFRR-CM. You can place bids up to {{days}} days ahead.', {
          days: Math.max(0, maxBidDaysAheadForActiveMarket ?? 0),
        })
      : t('The selected date is too far into the future for mFRR-EAM. You can place bids up to {{days}} days ahead.', {
          days: Math.max(0, maxBidDaysAheadForActiveMarket ?? 0),
        })
    : activeMarket === 'cm'
      ? t('The CM market is closed for the chosen date and you can no longer place bids')
      : t('The EAM market is closed for the chosen date and you can no longer place bids')

  return (
    <div className={styles.MFRRTradingViewHeader_Root}>
      <div className={styles.MFRRTradingViewHeader_Content}>
        <div className={styles.MFRRTradingViewHeader_TopArea}>
          <div className={styles.MFRRTradingViewHeader_MainColumn}>
            <div className={styles.MFRRTradingViewHeader_RootRow}>
              <div>
                <InfoBox infoHeader={infoHeader} onInfoButtonClick={onInfoButtonClick} beta={beta} />
              </div>
            </div>
            <div className={styles.MFRRTradingViewHeader_SecondaryChildrenContainer}>{secondaryChildren}</div>

            <div className={styles.MFRRTradingViewHeader_SwitchRow}>
              <div className={styles.MFRRTradingViewHeader_Switch}>
                <div className="flex h-[44px] items-center">
                  <Switch value={switchValue} onClick={onSwitchValueChange} items={switchItems} />
                </div>
              </div>
              <div
                className={`${styles.MFRRTradingViewHeader_CustomDatePicker} ${switchValue === 'custom' ? styles.MFRRTradingViewHeader_CustomDatePickerVisible : ''} [&_label:empty]:hidden [&_[role=group]]:h-auto [&_[role=group]>div]:py-[5px]`}
              >
                {switchValue === 'custom' && (
                  <DatePicker
                    value={date.startTime}
                    granularity="day"
                    minValue={Datetime.toISOString('2020-01-01T00:00:00+00:00')}
                    onChange={(value) => {
                      if (value) {
                        setDate(Datetime.getStartOfDay(value), Datetime.getEndOfDay(value))
                      }
                    }}
                  />
                )}
              </div>
              <div className={styles.MFRRTradingViewHeader_Date}>
                {Datetime.toLocalTime(date.startTime, 'onlyDate')}
              </div>
            </div>

            {shouldShowWarning && (
              <div className={`${styles.MFRRTradingViewHeader_Warning} ${styles.MFRRTradingViewHeader_WarningVisible}`}>
                <div className="w-fit">
                  <InfoBanner text={warningText} style="warning" />
                </div>
              </div>
            )}
            {hasDeadlineWarning && (
              <div className={`${styles.MFRRTradingViewHeader_Warning} ${styles.MFRRTradingViewHeader_WarningVisible}`}>
                <div className="w-fit">
                  <InfoBanner
                    text={t('There are unlocked values that should be locked before market close')}
                    style="danger"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {children && <div className={styles.MFRRTradingViewHeader_TabsRow}>{children}</div>}
      </div>

      <div className={styles.MFRRTradingViewHeader_DeadlineColumn}>
        <Deadline onWarningChange={handleDeadlineWarningChange} />
      </div>
    </div>
  )
}
